import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Shield,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  serviceInterest: string;
  message: string;
}

const whatHappensNext = [
  { step: '01', text: 'We review your message within 1 business day.' },
  { step: '02', text: 'We reply with clear next steps — no jargon, no pressure.' },
  { step: '03', text: "If it's a fit, we schedule your free CyvaCheck at a time that works for you." },
];

const Contact: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/contact`, data, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      toast.success('Message sent! We\'ll be in touch within 1 business day.');
      reset();
      setSubmitted(true);
    } catch (error: any) {
      const isAxios = axios.isAxiosError(error);
      if (isAxios && error.request && !error.response) {
        toast.error('Network error — please try emailing us directly at info@cyvantixsolutions.com');
      } else {
        toast.error('Something went wrong. Please email us at info@cyvantixsolutions.com');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0B8A8F] focus:border-transparent transition-all ${
      hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
    }`;

  return (
    <div className="w-full">

      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="bg-[#001a4d] py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-5">
            <Shield size={14} className="text-[#0B8A8F]" />
            <span className="text-white/80 text-xs font-bold uppercase tracking-widest">Get In Touch</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
            Let's talk about your security
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-xl mx-auto">
            Book a free CyvaCheck, ask about a service, or just tell us what's on your mind.
            We respond within 1 business day.
          </p>
        </div>
      </section>

      {/* ── BODY ────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* ── LEFT: form (3 cols) ── */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              {submitted ? (
                /* Success state */
                <div className="flex flex-col items-center justify-center text-center h-full py-10">
                  <div className="w-16 h-16 rounded-2xl bg-[#0B8A8F] flex items-center justify-center mb-5">
                    <CheckCircle className="w-8 h-8 text-white" strokeWidth={2} />
                  </div>
                  <h2 className="text-2xl font-extrabold text-[#001a4d] mb-3">Message received!</h2>
                  <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                    We'll be in touch within 1 business day. In the meantime, feel free to email us
                    directly at{' '}
                    <a href="mailto:info@cyvantixsolutions.com" className="text-[#0B8A8F] font-semibold">
                      info@cyvantixsolutions.com
                    </a>.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-sm text-gray-400 hover:text-[#001a4d] underline transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-extrabold text-[#001a4d] mb-1">Send us a message</h2>
                  <p className="text-gray-500 text-sm mb-7">
                    Fill out the form and we'll get back to you within 1 business day.
                  </p>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {/* Name row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                          First Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('firstName', { required: 'Required' })}
                          type="text"
                          placeholder="Jane"
                          className={inputClass(!!errors.firstName)}
                        />
                        {errors.firstName && (
                          <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                          Last Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('lastName', { required: 'Required' })}
                          type="text"
                          placeholder="Smith"
                          className={inputClass(!!errors.lastName)}
                        />
                        {errors.lastName && (
                          <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Email + Company */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                          Email <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('email', {
                            required: 'Required',
                            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                          })}
                          type="email"
                          placeholder="jane@smithlaw.com"
                          className={inputClass(!!errors.email)}
                        />
                        {errors.email && (
                          <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                          Business Name
                        </label>
                        <input
                          {...register('company')}
                          type="text"
                          placeholder="Smith Law Firm"
                          className={inputClass(false)}
                        />
                      </div>
                    </div>

                    {/* Service interest */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                        What can we help with? <span className="text-red-400">*</span>
                      </label>
                      <select
                        {...register('serviceInterest', { required: 'Please select an option' })}
                        defaultValue=""
                        className={inputClass(!!errors.serviceInterest)}
                      >
                        <option value="" disabled>Select a service...</option>
                        <optgroup label="Cybersecurity & Compliance">
                          <option value="CyvaCheck (Free 20-min check)">CyvaCheck — Free 20-min security check</option>
                          <option value="CyvaScore (Full assessment)">CyvaScore — Full security assessment</option>
                          <option value="CyvaGuard Essentials (Monthly monitoring)">CyvaGuard Essentials — Monthly monitoring</option>
                          <option value="CyvaGuard Pro (Advanced monitoring)">CyvaGuard Pro — Advanced monitoring</option>
                          <option value="CyvaComply (Compliance gap closure)">CyvaComply — Compliance gap closure</option>
                          <option value="CyvaAware (Staff workshop)">CyvaAware — AI threat staff workshop</option>
                        </optgroup>
                        <optgroup label="IT Consulting">
                          <option value="IT Setup and Support">IT Setup and Support</option>
                          <option value="Network Management">Network Management</option>
                          <option value="Combined IT & Security Package">Combined IT & Security Package</option>
                        </optgroup>
                        <option value="Not sure — need guidance">Not sure — need guidance</option>
                      </select>
                      {errors.serviceInterest && (
                        <p className="text-red-500 text-xs mt-1">{errors.serviceInterest.message}</p>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                        Message <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        {...register('message', {
                          required: 'Required',
                          minLength: { value: 10, message: 'Please add a bit more detail' },
                        })}
                        rows={4}
                        placeholder="Tell us a little about your business and what you're looking for..."
                        className={inputClass(!!errors.message)}
                      />
                      {errors.message && (
                        <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0B8A8F] text-white font-bold rounded-lg hover:bg-[#097a7f] transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                    >
                      {isSubmitting ? 'Sending...' : (
                        <>Send Message <ArrowRight size={16} /></>
                      )}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      By submitting, you agree to be contacted by Cyvantix Solutions regarding your request.
                    </p>
                  </form>
                </>
              )}
            </div>

            {/* ── RIGHT: info panel (2 cols) ── */}
            <div className="lg:col-span-2 flex flex-col gap-6">

              {/* Contact details */}
              <div className="bg-[#001a4d] rounded-2xl p-6 text-white">
                <h3 className="font-extrabold text-base mb-4">Contact details</h3>
                <ul className="space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Mail size={14} className="text-[#0B8A8F]" />
                    </div>
                    <div>
                      <div className="text-white/50 text-xs mb-0.5">Email</div>
                      <a href="mailto:info@cyvantixsolutions.com" className="text-white hover:text-[#0B8A8F] transition-colors font-medium">
                        info@cyvantixsolutions.com
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-[#0B8A8F]" />
                    </div>
                    <div>
                      <div className="text-white/50 text-xs mb-0.5">Location</div>
                      <span className="text-white font-medium">Baltimore, MD</span>
                      <div className="text-white/50 text-xs mt-0.5">Serving Maryland & surrounding region</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock size={14} className="text-[#0B8A8F]" />
                    </div>
                    <div>
                      <div className="text-white/50 text-xs mb-0.5">Response time</div>
                      <span className="text-white font-medium">Within 1 business day</span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* What happens next */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="font-extrabold text-[#001a4d] text-base mb-4">What happens next</h3>
                <ol className="space-y-4">
                  {whatHappensNext.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#001a4d] text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Quick CyvaCheck shortcut */}
              <div className="bg-[#0B8A8F] rounded-2xl p-6 text-white text-center">
                <Shield size={28} className="mx-auto mb-3 text-white/80" />
                <p className="font-bold text-sm mb-1">Want to skip the form?</p>
                <p className="text-white/70 text-xs mb-4">
                  Email us directly to book your free CyvaCheck.
                </p>
                <a
                  href="mailto:info@cyvantixsolutions.com?subject=Book%20Free%20CyvaCheck"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#0B8A8F] font-bold rounded-lg hover:bg-gray-50 transition-all text-sm"
                >
                  Book Free CyvaCheck <ArrowRight size={14} />
                </a>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
